import {afterNextRender, Component, computed, effect, inject, OnInit, PLATFORM_ID} from '@angular/core';
import {AuctionsHeroComponent} from './auctions-components/auctions-hero/auctions-hero.component';
import {AuctionsOfferComponent} from './auctions-components/auctions-offer/auctions-offer.component';
import {AuctionsAboutComponent} from './auctions-components/auctions-about/auctions-about.component';
import {AuctionsFormComponent} from './auctions-components/auctions-form/auctions-form.component';
import {FaqComponent} from '../shared/component/faq/faq.component';
import {AuctionsSolutionsComponent} from './auctions-components/auctions-solutions/auctions-solutions.component';
import {FooterComponent} from '../shared/component/footer/footer.component';
import {SectionDirective} from '../shared/directive/section.directive';
import {AbstractLandingComponent} from '../shared/component/abstract/abstract-landing.component';
import {AuctionsStepsComponent} from './auctions-components/auctions-steps/auctions-steps.component';
import {AuctionsBaitComponent} from './auctions-components/auctions-bait/auctions-bait.component';
import {landingSectionsSignal} from '../shared/signal/common/landing-sections.signal';
import {ComponentMetadata} from '../shared/model/component-metadata.model';
import {ActivatedRoute} from '@angular/router';
import {SeoService} from '../shared/service/seo.service';
import {AnalyticsEventCategory, AnalyticsEventName} from '../shared/enum/gtm-cookie-consent.enum';
import {MicrosoftClarityService} from '../shared/analytics/microsoft/microsoft-clarity.service';
import {GoogleAnalyticsService} from '../shared/analytics/google/google-analytics.service';
import {formComponentSignal} from '../shared/signal/common/form-component.signal';
import {utmResolver} from '../shared/resolver/utm.resolver';
import {isPlatformBrowser} from '@angular/common';
import {routingMetadata} from '../shared/signal/common/routing-matadata.signal';

@Component({
  selector: 'app-auctions.component',
  imports: [
    AuctionsHeroComponent,
    AuctionsOfferComponent,
    AuctionsAboutComponent,
    AuctionsFormComponent,
    FaqComponent,
    AuctionsSolutionsComponent,
    FooterComponent,
    SectionDirective,
    AuctionsStepsComponent,
    AuctionsBaitComponent
  ],
  templateUrl: './auctions.component.html',
  styleUrl: './auctions.component.scss'
})
export class AuctionsComponent extends AbstractLandingComponent implements OnInit {

  protected readonly computedSections = computed<ComponentMetadata[]>(() => landingSectionsSignal());
  public readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly pageTitle = computed(() => routingMetadata().label || 'ProcessWay AutoPark')
  private readonly locale = $localize.locale || 'pl-PL';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly seoService: SeoService,
    private readonly microsoftClarityService: MicrosoftClarityService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
  ) {
    super();

    this.initSeoTags();

    effect(() => {
      const sections = this.computedSections();
      if (!sections?.length) return;

      const formSection = sections.find(s => s.id === 'application-form');

      formComponentSignal.set(formSection);
    });

    this.registerMicrosoftClaritySessionMeta();
  }

  public ngOnInit(): void {
    if (this.isBrowser) {
      this.trackPageViewEvent();
    }
  }

  private trackPageViewEvent(): void {
    this.googleAnalyticsService.trackEvent({
      event: AnalyticsEventName.PageView,
      eventCategory: AnalyticsEventCategory.RouteEvent,
      eventPayload: {
        page_location: window.location.href,
        language: this.locale,
        page_title: this.pageTitle()
      }
    });
  }

  private registerMicrosoftClaritySessionMeta(): void {
    afterNextRender(() => {
      this.microsoftClarityService.identify('autopark', undefined, undefined, 'Autopark Landing');
      this.microsoftClarityService.setTag('variant', 'auctions');

      const utm = utmResolver('utm_source');

      if (utm) {
        this.microsoftClarityService.setTag('utm_source', utm);
      }
    });
  }

  private initSeoTags() {
    let deepest = this.route;
    while (deepest.firstChild) deepest = deepest.firstChild;
    const routingData = deepest.snapshot.data;

    this.seoService.init(routingData);
  }
}
